/**
 * Tiny source↔model mapping for board→editor navigation.
 *
 * Given a board selection id (a `#id`, a qualified key like `Platform.Auth`, or
 * a bare name) find where its declaration sits in the workspace text, so the
 * editor can reveal it. This is a heuristic text scan (same approach as Studio's
 * EditorNav `declLine`), deliberately decoupled from the resolved model — it
 * needs only the files, not a compile. Full model-driven mapping is the Smart
 * View follow-up.
 */
import type { Vfs } from "@archlang/viewer-core";

export interface DeclLocation {
  readonly path: string;
  /** 1-based line of the declaration. */
  readonly line: number;
}

// The token right before a name is a *usage* (control flow / clause), not a
// declaration — so `do Orders`, `each Orders`, `use X from Orders`, `in Orders`
// must NOT be read as declaring `Orders`.
const NON_DECL_KW = new Set([
  "do", "each", "if", "else", "select", "one", "try", "catch", "parallel",
  "branch", "join", "race", "out", "await", "reversible", "unwind", "on", "go", "dist",
  "return", "fail", "finish", "use", "from", "export", "in", "by",
  "group", "focus", "layout", "include", "exclude", "aspect", "view",
  "required", "cascade", "append", "override", "drop", "and", "or",
]);
// Identifier classes MIRROR the lexer's v0.8.2 unicode shape (UAX #31):
// non-ASCII scripts are ordinary identifiers (`Платежи`, `注文サービス` —
// SPECIFICATION.md §5.1). ASCII-only classes here left editor→board framing
// silently dead for unicode-named modules (r45). `_` stays admitted for the
// heuristic's tolerance; dots admit qualified names.
const ID_START = "[A-Za-z_\\p{ID_Start}]";
const ID_CONT = "[\\w\\p{ID_Continue}]";
const IDENT_DOTTED = `${ID_START}(?:${ID_CONT}|\\.)*`;
const IDENT = new RegExp(`^${IDENT_DOTTED}$`, "u");

/** Reduce a board id to the simple name a declaration would use. */
export function simpleName(id: string): string {
  const noHash = id.startsWith("#") ? id.slice(1) : id;
  const seg = noHash.split(".").pop() ?? noHash;
  return seg.trim();
}

// Blank out string literals so a name inside a description (`"the Orders flow"`)
// is never matched as its declaration.
function stripStrings(line: string): string {
  return line.replace(/"(?:\\.|[^"\\])*"/g, '""');
}

/**
 * Find the first declaration of `name` across the workspace. Two passes: a
 * `<kind> Name` declaration (high confidence, with the kind not being a
 * control/clause keyword), then any standalone `Name` token (bodyless decl /
 * last resort). Files are scanned in sorted order so the result is stable when
 * a leaf name collides across files. Returns null if not found.
 */
export function findDeclaration(files: Vfs, idOrName: string): DeclLocation | null {
  const name = simpleName(idOrName);
  if (!name) return null;
  const esc = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Trailing boundary: `\b` is dead after a non-ASCII char, so bound on
  // "not an identifier-continue char" instead (r45).
  const BOUND = `(?!${ID_CONT})`;
  const declRe = new RegExp(`(?:^|\\s)(${IDENT_DOTTED})\\s+${esc}${BOUND}`, "u");
  // A stable id never appears bare in source — it's glued to its sigil
  // (`service #pay001 Payments`). A board id for such a module IS the bare id
  // (`pay001`), so also accept the `#id` token as a declaration marker.
  const sigilRe = new RegExp(`(?:^|\\s)#${esc}${BOUND}`, "u");
  const bareRe = new RegExp(`(?:^|\\s)${esc}${BOUND}`, "u");
  const paths = [...files.keys()].filter(isArch).sort();

  // Pass 1: `kind Name` or a `#id` sigil token (a real declaration line).
  for (const path of paths) {
    const lines = (files.get(path) ?? "").split("\n");
    for (let i = 0; i < lines.length; i++) {
      if (isComment(lines[i])) continue;
      const line = stripStrings(lines[i]);
      if (sigilRe.test(line)) return { path, line: i + 1 };
      const m = declRe.exec(line);
      if (m && IDENT.test(m[1]) && !NON_DECL_KW.has(m[1])) return { path, line: i + 1 };
    }
  }
  // Pass 2: any line mentioning the bare name (string- and comment-stripped).
  for (const path of paths) {
    const lines = (files.get(path) ?? "").split("\n");
    for (let i = 0; i < lines.length; i++) {
      if (isComment(lines[i])) continue;
      if (bareRe.test(stripStrings(lines[i]))) return { path, line: i + 1 };
    }
  }
  return null;
}

/**
 * Names declared within a line range of one file — for "frame what I selected in
 * the editor". Scans `[startLine, endLine]` (1-based, inclusive) for declaration
 * headers `<kind> <Name>` where the kind isn't a control/clause keyword, and
 * returns each declared name once. A heuristic text scan (same family as
 * `findDeclaration`), deliberately model-free: the caller resolves these names
 * to board ids against the compiled model.
 *
 * Skips comments and blanks string literals, so a name inside a description
 * isn't mistaken for a declaration. Field/step lines (`team: x`, `A > B`) don't
 * match the `kind Name` shape, so they're naturally ignored.
 */
export function declaredNamesInRange(
  fileText: string,
  startLine: number,
  endLine: number,
): string[] {
  const lines = fileText.split("\n");
  const lo = Math.max(1, Math.min(startLine, endLine));
  const hi = Math.min(lines.length, Math.max(startLine, endLine));
  const out: string[] = [];
  const seen = new Set<string>();
  // Optional declaration prefixes that precede the kind keyword.
  const PREFIX = /^(?:required|override|drop|export)\s+/;
  for (let i = lo; i <= hi; i++) {
    const raw = lines[i - 1];
    if (raw === undefined || isComment(raw)) continue;
    let line = stripStrings(raw).trim();
    while (PREFIX.test(line)) line = line.replace(PREFIX, "");
    // `<kind> [#id] <Name>` — two bare identifiers, with an optional stable-id
    // token (`service #be002 Checkout`) allowed between them. A field (`k: v`)
    // breaks on the colon; a step (`A > B`) breaks on `>`; both fail this shape.
    const m = new RegExp(`^(${IDENT_DOTTED})\\s+(?:#[\\w-]+\\s+)?(${IDENT_DOTTED})`, "u").exec(line);
    if (!m) continue;
    const [, kind, name] = m;
    if (!kind || !name) continue;
    // Only the KIND side filters keywords (`do Orders` declares nothing) —
    // the NAME side must not: `service view {}` is a legal keyword-named
    // module whose framing was silently dead (r46).
    if (NON_DECL_KW.has(kind)) continue;
    if (!seen.has(name)) {
      seen.add(name);
      out.push(name);
    }
  }
  return out;
}

/**
 * Identifier tokens inside an exact editor selection (1-based lines/columns,
 * Monaco convention: endColumn is the position AFTER the last selected char).
 * Complements `declaredNamesInRange` for *references*: a bare step
 * (`Shortener > Keygen`) declares nothing, but selecting `Shortener` — or the
 * whole step — should still frame the module(s) it names, including stubs that
 * exist only because a step synthesized them. The caller resolves each token
 * against the compiled model, so prose words that aren't module names fall
 * away naturally.
 *
 * Line comments are cut before tokenizing so `Shortener > Keygen // mint a
 * code` doesn't leak `mint`/`code` as candidate names.
 */
export function referencedNamesInSelection(
  fileText: string,
  startLine: number,
  startColumn: number,
  endLine: number,
  endColumn: number,
): string[] {
  const lines = fileText.split("\n");
  const lo = Math.max(1, Math.min(startLine, endLine));
  const hi = Math.min(lines.length, Math.max(startLine, endLine));
  const out: string[] = [];
  const seen = new Set<string>();
  for (let i = lo; i <= hi; i++) {
    let raw = lines[i - 1] ?? "";
    // Slice to the selected span on the boundary lines (whole line between).
    const from = i === lo ? startColumn - 1 : 0;
    const to = i === hi ? endColumn - 1 : raw.length;
    raw = raw.slice(Math.max(0, from), Math.max(0, to));
    // Drop line comments, then tokenize identifiers.
    const code = raw.split("//")[0] ?? "";
    for (const tok of code.match(new RegExp(`${ID_START}${ID_CONT}*`, "gu")) ?? []) {
      // No keyword filter: a module legally named `view`/`go` must still
      // frame (r46) — the caller resolves tokens against the compiled model,
      // so control-flow words that name no module fall away naturally.
      if (seen.has(tok)) continue;
      seen.add(tok);
      out.push(tok);
    }
  }
  return out;
}

/**
 * Names of the declarations enclosing `line` (1-based), innermost first — the
 * brace stack at that line. Used to answer "did the caret land INSIDE the node
 * we framed?" when a selection collapses: caret inside → grace the glide-back,
 * caret elsewhere → return immediately.
 *
 * Tracks multiline strings (a `note` body is one) by toggling on unpaired
 * quotes per line, so braces inside prose (e.g. a `~{ … }` calc region) don't
 * corrupt the stack. Heuristic like the rest of this module — no compile.
 */
export function enclosingDeclarations(fileText: string, line: number): string[] {
  const lines = fileText.split("\n");
  const last = Math.min(line, lines.length);
  const stack: string[] = [];
  // A declaration whose header IS the caret line encloses the caret even when
  // its block also closes on that line (a one-liner `svc X {}`) — captured
  // separately so the same-line `}` can't pop it out of the stack.
  let caretDeclares: string | null = null;
  const PREFIX = /^(?:required|override|drop|export)\s+/;
  let inString = false;
  for (let i = 1; i <= last; i++) {
    const raw = lines[i - 1] ?? "";
    if (!inString && isComment(raw)) continue;
    // Walk the line char-by-char: toggle string state on unescaped quotes,
    // and only count braces / read headers outside strings.
    let visible = "";
    for (let c = 0; c < raw.length; c++) {
      const ch = raw[c];
      if (ch === '"' && raw[c - 1] !== "\\") {
        inString = !inString;
        continue;
      }
      if (!inString) visible += ch;
    }
    // The caret line itself: its own braces don't change what ENCLOSES it
    // when the header opens here (a `kind Name {` line is inside that decl).
    let header = visible.trim();
    while (PREFIX.test(header)) header = header.replace(PREFIX, "");
    const m = new RegExp(`^(${IDENT_DOTTED})\\s+(?:#[\\w-]+\\s+)?(${IDENT_DOTTED})`, "u").exec(header);
    const declares =
      // KIND-side keyword filter only — keyword-NAMED decls still enclose (r46).
      m && m[1] && m[2] && !NON_DECL_KW.has(m[1]) && visible.includes("{")
        ? m[2]
        : null;
    // The caret line: a declaration opened here encloses the caret regardless
    // of whether its block also closes on this line — record it and stop before
    // this line's braces (which would otherwise balance out and drop it). A
    // non-declaring caret line still walks its braces so a caret on a body /
    // closing line reflects the real nesting.
    if (i === last) {
      if (declares) caretDeclares = declares;
      else {
        for (const ch of visible) {
          if (ch === "{") stack.push("");
          else if (ch === "}") stack.pop();
        }
      }
      break;
    }
    let firstBrace = true;
    for (const ch of visible) {
      if (ch === "{") {
        stack.push(firstBrace && declares ? declares : "");
        firstBrace = false;
      } else if (ch === "}") stack.pop();
    }
  }
  const enclosing = [...stack].reverse().filter((n) => n !== "");
  return caretDeclares ? [caretDeclares, ...enclosing] : enclosing;
}

function isArch(path: string): boolean {
  return path.endsWith(".arch") || path.endsWith(".archspace");
}

function isComment(line: string): boolean {
  const t = line.trimStart();
  return t.startsWith("//") || t.startsWith("*") || t.startsWith("/*");
}
